<script setup lang="ts">
import {
	computed,
	ref
} from "vue"
import { useRoute } from "vue-router"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppLoader from "@/components/common/AppLoader.vue"
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue"
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue"
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue"
import JobInfoModal from "@/components/main/jobs-panel/modals/JobInfoModal.vue"
import NotificationUndoOverlay from "./NotificationUndoOverlay.vue"
import { JobNotification } from "@/ api/NotificationsApi"
import { JobStatus } from "@/models/etc/types"
import { workerStore } from "@/store/worker"
import { jobsStore } from "@/store/jobs"
import { headerStore, JobNotificationAction } from "@/store/header"
import { trimTrailingLineBreaks } from "@/utils/textHelpers"
import { i18n } from "@/i18n"

const t = (key: string, named?: Record<string, unknown>): string => String(named ? i18n.global.t(key, named) : i18n.global.t(key))

type Props = {
	notification: JobNotification
	showMoveAction?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	showMoveAction: false
})

const emit = defineEmits<{
	(e: 'move-to-history', id: number): void
}>()

const route = useRoute()
const worker = workerStore()
const header = headerStore()

const $jobInfoModal = ref<InstanceType<typeof JobInfoModal> | null>(null)
const $commentTextarea = ref<InstanceType<typeof AppCustomTextarea> | null>(null)

const isLoading = ref(false)
const showCommentInput = ref(false)
const commentMessage = ref('')
const pendingAction = ref<JobNotificationAction | null>(null)
const additionsExpanded = ref(false)
const isClientsExpanded = ref(false)

const displayedClients = computed(() =>
	isClientsExpanded.value || !props.notification.Clients
		? props.notification.Clients
		: props.notification.Clients.slice(0, 1)
)

const userId = computed(() => worker.workerProfile?.Id)
const isAdmin = computed(() => worker.workerProfile?.IsAdmin ?? false)
const isCreator = computed(() => props.notification.Creator?.Id === userId.value)
const isWorker = computed(() => props.notification.Worker?.Id === userId.value)

const showCreatorName = computed(() =>
	!!props.notification.Creator && props.notification.Creator.Id !== props.notification.Worker?.Id
)

const isWorkingOrLost = computed(() =>
	props.notification.Status === JobStatus.Working || props.notification.Status === JobStatus.Lost
)
const isWaitingForCheck = computed(() =>
	props.notification.Status === JobStatus.WaitingForCheck
)

const showSendToChecking = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && !isCreator.value && props.notification.NeedChecking
)
const showEndByWorkerNoMessage = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && !isCreator.value && !props.notification.NeedChecking
)
const showSelfWorkerWithReport = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && isCreator.value && props.notification.NeedChecking
)
const showSelfWorkerNoReport = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && isCreator.value && !props.notification.NeedChecking
)
const showCreatorChecking = computed(() =>
	!showCommentInput.value && isWaitingForCheck.value && isCreator.value && !isWorker.value
)
const showAdminEnd = computed(() =>
	!showCommentInput.value && isAdmin.value && !isCreator.value && !isWorker.value
	&& (isWorkingOrLost.value || isWaitingForCheck.value)
)
const showAdminEndExtra = computed(() => {
	if (showCommentInput.value || !isAdmin.value) return false
	if (isWorkingOrLost.value && isWorker.value && !isCreator.value && props.notification.NeedChecking) return true
	if (isWorkingOrLost.value && isCreator.value && !isWorker.value) return true
	if (isWaitingForCheck.value && isWorker.value && !isCreator.value && props.notification.NeedChecking) return true
	return false
})

const hasActionButtons = computed(() =>
	showSendToChecking.value || showEndByWorkerNoMessage.value || showSelfWorkerWithReport.value
	|| showSelfWorkerNoReport.value || showCreatorChecking.value || showAdminEnd.value
	|| showAdminEndExtra.value
)

const commentLabel = computed(() => {
	switch (pendingAction.value) {
		case 'endByWorker': return t('job_comment_label_finish') + ':'
		case 'sendToChecking': return t('job_comment_label_report') + ':'
		case 'sendToReworking': return t('job_action_rework') + ':'
		case 'endByAdmin': return t('job_comment_label_finish') + ':'
		default: return ''
	}
})

const getJobStatusBarColor = (status: number) => {
	switch (status) {
		case JobStatus.Standard: return "#9A9C9E"
		case JobStatus.Working: return "#86CED2"
		case JobStatus.WaitingForCheck: return "#FFE7AA"
		case JobStatus.Completed: return "#6DA279"
		case JobStatus.Lost: return "#EE966A"
		default: return "#9A9C9E"
	}
}

const formatDate = (date?: string | null) => {
	if (!date) return ""
	const dateObj = new Date(date)
	return dateObj.toLocaleDateString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit"
	})
}
// Время в формате "чч:мм" — рисуется рядом с датой создания, но своим цветом
const formatTime = (date?: string | null) => {
	if (!date) {
		return ""
	}

	const dateObj = new Date(date)
	const hours = String(dateObj.getHours()).padStart(2, '0')
	const minutes = String(dateObj.getMinutes()).padStart(2, '0')
	return `${hours}:${minutes}`
}
const formatDateTime = (date?: string | null) => {
	if (!date) return ""
	const dateObj = new Date(date)
	const hours = String(dateObj.getHours()).padStart(2, '0')
	const minutes = String(dateObj.getMinutes()).padStart(2, '0')
	const day = String(dateObj.getDate()).padStart(2, '0')
	const month = String(dateObj.getMonth() + 1).padStart(2, '0')
	const year = dateObj.getFullYear()
	return `${hours}:${minutes} ${day}.${month}.${year}`
}

const showUserJobs = (id?: number) => {
	if (id == null) return
	jobsStore().setUserForUpdateJobsList(id)
}

const startAction = (action: JobNotificationAction) => {
	showCommentInput.value = true
	pendingAction.value = action
	commentMessage.value = ''
}

const cancelAction = () => {
	showCommentInput.value = false
	commentMessage.value = ''
	pendingAction.value = null
	$commentTextarea.value?.reset()
}

const directAction = async (action: JobNotificationAction) => {
	try {
		isLoading.value = true
		await header.applyJobAction(props.notification.Id, props.notification.JobId, action)
	} finally {
		isLoading.value = false
	}
}

const submitAction = async () => {
	if (!pendingAction.value || !commentMessage.value.trim()) return
	const message = trimTrailingLineBreaks(commentMessage.value)
		.replace(/\n/g, '<br/>')
	const action = pendingAction.value
	cancelAction()
	try {
		isLoading.value = true
		await header.applyJobAction(props.notification.Id, props.notification.JobId, action, message)
	} finally {
		isLoading.value = false
	}
}

const handleAdminEnd = () => {
	if (props.notification.NeedChecking) {
		startAction('endByAdmin')
	} else {
		directAction('endByAdmin')
	}
}

const handleShowInfo = () => {
	$jobInfoModal.value?.onOpen(props.notification.JobId)
}

const isChecked = ref(false)
const $undoOverlay = ref<InstanceType<typeof NotificationUndoOverlay> | null>(null)

const onCheck = (val: boolean) => {
	if (!val) {
		return
	}
	isChecked.value = true
}

const onMoveCountdownFinish = () => {
	emit('move-to-history', props.notification.Id)
}

const onCancelMove = () => {
	isChecked.value = false
}

const flushPendingMove = () => {
	$undoOverlay.value?.flush()
}

defineExpose({ flushPendingMove })

const loaderStyle = { 'background-color': 'rgba(255, 255, 255, 0.5)' }
</script>

<template>
	<job-info-modal ref="$jobInfoModal" />
	<div class="notification-row">
		<div v-if="props.showMoveAction" class="notification-row__check">
			<app-tooltip
				:text="$t('notification_move_to_history')"
				:disabled="isChecked"
			>
				<template #activator>
					<app-checkbox
						shape="round"
						:value="isChecked"
						:disabled="isChecked"
						@update:value="onCheck"
					/>
				</template>
			</app-tooltip>
		</div>
		<div class="notification-item">
			<app-loader v-if="isLoading" :style="loaderStyle" />
			<div
				class="notification-item__colored"
				:style="{ backgroundColor: getJobStatusBarColor(props.notification.Status) }"
			/>
			<div class="notification-item__header">
				<div class="notification-item__header-workers">
					<div
						v-if="props.notification.Worker"
						class="notification-item__header-worker"
						@click="showUserJobs(props.notification.Worker.Id)"
					>
						{{ props.notification.Worker.Name }}
					</div>
					<template v-if="showCreatorName">
						<div class="notification-item__header-divider">
							<app-icon
								name="chevron-left"
								width="16px"
								color="#499259"
							/>
						</div>
						<div
							class="notification-item__header-worker"
							@click="showUserJobs(props.notification?.Creator?.Id)"
						>
							{{ props.notification?.Creator?.Name }}
						</div>
					</template>
				</div>
				<div class="notification-item__header-options">
					<div v-if="hasActionButtons" class="notification-item__actions">
						<template v-if="showSendToChecking">
							<app-tooltip :text="$t('job_action_send_to_checking')">
								<template #activator>
									<app-icon
										name="send-to-check"
										width="18px"
										color="#979797"
										hover-color="#F9C22F"
										cursor="pointer"
										@click="startAction('sendToChecking')"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showEndByWorkerNoMessage">
							<app-tooltip :text="$t('job_action_finish')">
								<template #activator>
									<app-icon
										name="check-circle"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="directAction('endByWorker')"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showSelfWorkerWithReport">
							<app-tooltip :text="$t('job_action_finish_with_report')">
								<template #activator>
									<app-icon
										name="check-circle"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="startAction('endByWorker')"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showSelfWorkerNoReport">
							<app-tooltip :text="$t('job_action_finish')">
								<template #activator>
									<app-icon
										name="check-circle"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="directAction('endByWorker')"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showCreatorChecking">
							<app-tooltip :text="$t('job_action_accept')">
								<template #activator>
									<app-icon
										name="check-circle"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="directAction('endByCreator')"
									/>
								</template>
							</app-tooltip>
							<app-tooltip :text="$t('job_action_rework')">
								<template #activator>
									<app-icon
										name="back-to-rework"
										width="18px"
										color="#979797"
										hover-color="#F9C22F"
										cursor="pointer"
										@click="startAction('sendToReworking')"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showAdminEnd">
							<app-tooltip :text="$t('job_action_finish_as_admin')">
								<template #activator>
									<app-icon
										name="end-by-admin"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="handleAdminEnd"
									/>
								</template>
							</app-tooltip>
						</template>
						<template v-if="showAdminEndExtra">
							<app-tooltip :text="$t('job_action_finish_as_admin')">
								<template #activator>
									<app-icon
										name="end-by-admin"
										width="18px"
										color="#979797"
										hover-color="#607D8B"
										cursor="pointer"
										@click="handleAdminEnd"
									/>
								</template>
							</app-tooltip>
						</template>
					</div>
					<app-tooltip :text="$t('job_info_title')">
						<template #activator>
							<app-icon
								name="info-circle-outline"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="handleShowInfo"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>
			<div class="notification-item__body">
				<div
					class="notification-item__body-message"
					v-html="props.notification.Message"
				/>
				<div
					v-if="props.notification.Additions && props.notification.Additions.length >= 2 && !additionsExpanded"
					class="notification-item__additions-collapsed"
					@click="additionsExpanded = true"
				>
					<app-icon name="plus-circle" width="16px" color="#979797" cursor="pointer" />
					<span>
						{{ props.notification.Additions.length }}
						{{ $tc('job_comments_count', props.notification.Additions.length ?? 0) }}
					</span>
				</div>
				<div
					v-else-if="props.notification.Additions && props.notification.Additions.length"
					class="notification-item__additions"
				>
					<div
						v-for="addition in props.notification.Additions"
						:key="addition.Date"
						class="notification-item__addition"
					>
						<div class="notification-item__addition-header">
							<span class="notification-item__addition-user">{{ addition.Username }}</span>
							<span class="notification-item__addition-date">{{ formatDateTime(addition.Date) }}</span>
						</div>
						<div class="notification-item__addition-message">
							<b class="notification-item__addition-title">{{ addition.Title }}</b>&nbsp;<span v-html="addition.Message" />
						</div>
					</div>
				</div>
				<div
					v-if="showCommentInput"
					class="notification-item__comment"
					@keydown.escape="cancelAction"
				>
					<span class="notification-item__comment-label">{{ commentLabel }}</span>
					<div class="notification-item__comment-input">
						<app-custom-textarea
							ref="$commentTextarea"
							v-model="commentMessage"
							:placeholder="$t('ui_enter_text')"
							:min-height="32"
							:max-height="200"
							:auto-focus="true"
							@ctrl-enter="submitAction"
						/>
						<app-tooltip :text="$t('ui_cancel')">
							<template #activator>
								<app-icon
									name="close-bold"
									width="16px"
									color="#979797"
									hover-color="#BA4C4C"
									cursor="pointer"
									:style="{ marginTop: '-4px' }"
									@click="cancelAction"
								/>
							</template>
						</app-tooltip>
						<app-tooltip :text="$t('send_message')">
							<template #activator>
								<app-icon
									name="send-message"
									width="16px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									:style="{ marginTop: '-4px' }"
									@click="submitAction"
								/>
							</template>
						</app-tooltip>
					</div>
				</div>
				<div
					v-if="props.notification.Project"
					class="notification-item__body-project"
				>
					<span class="notification-item__body-project-text">{{ $t('project_label') }}:&nbsp;</span>
					<router-link
						class="notification-item__body-project-link"
						:to="{ query: { page: 'project', id: props.notification.Project.Id } }"
					>
						{{ props.notification.Project.Name }}
					</router-link>
				</div>
				<div
					v-if="props.notification.Clients && props.notification.Clients.length"
					class="notification-item__body-project"
				>
					<span class="notification-item__body-project-text">
						{{ $t(props.notification.Clients.length === 1 ? 'client_label' : 'clients_label') }}:
					</span>
					<template
						v-for="(client, index) in displayedClients"
						:key="client.Id"
					>
						<router-link
							class="notification-item__body-project-link"
							:to="{ query: { page: 'client', id: client.Id } }"
						>
							{{ client.Name }}
						</router-link>
						<span
							v-if="index + 1 < displayedClients.length"
							class="notification-item__body-project-divider"
						>&nbsp;/&nbsp;</span>
					</template>
					<span
						v-if="!isClientsExpanded && props.notification.Clients.length > 1"
						class="notification-item__body-project-show-more"
						@click="isClientsExpanded = true"
					>
						{{ $t('more_count_label', { count: props.notification.Clients.length - 1 }) }}
					</span>
				</div>
			</div>
			<div class="notification-item__footer-bottom">
				<app-tooltip :text="$t(props.notification.Lost ? 'job_deadline' : 'job_completed_on_time')">
					<template #activator>
						<div
							:class="{
								'notification-item__footer-bottom-date': true,
								'notification-item__footer-bottom-date--lost': props.notification.Lost,
								'notification-item__footer-bottom-date--success': props.notification.Status === JobStatus.Completed && !props.notification.Lost
							}"
						>
							<app-icon
								v-if="props.notification.ToDoDate"
								class="notification-item__footer-bottom-date-clock"
								name="clock"
								width="16px"
							/>
							<template v-if="props.notification.ToDoDate">
								{{ formatDate(props.notification.ToDoDate) }}
							</template>
						</div>
					</template>
				</app-tooltip>
				<span>
					{{ formatDate(props.notification.CreateTime) }}
					<span class="notification-item__create-time">
						{{ formatTime(props.notification.CreateTime) }}
					</span>
				</span>
			</div>
			<notification-undo-overlay
				v-if="isChecked"
				ref="$undoOverlay"
				:text="$t('notification_moved_to_history')"
				@finish="onMoveCountdownFinish"
				@cancel="onCancelMove"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">
.notification-row {
	display: flex;
	align-items: center;
	gap: 8px;

	&__check {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}
}

.notification-item {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	position: relative;
	padding: 8px 8px 8px 16px;
	color: #4D4D4D;
	border-radius: 8px;
	background-color: #fff;

	&__colored {
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 4px;
		border-top-left-radius: 8px 12px;
		border-bottom-left-radius: 8px 12px;
	}

	&__header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 4px;
		gap: 8px;

		&-workers {
			display: inline;
			color: #438451;
			font-weight: 500;
			font-size: 14px;
			line-height: 18px;
			min-width: 0;
		}

		&-worker {
			display: inline;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}

		&-divider {
			display: inline;
		}

		&-options {
			display: flex;
			align-items: center;
			gap: 6px;
			flex-shrink: 0;
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	&__body {
		display: flex;
		flex-direction: column;
		margin-bottom: 4px;

		&-message {
			font-size: 14px;
			line-height: 18px;
			color: #333333;
			margin-bottom: 4px;
			word-break: break-word;
		}

		&-project {
			display: inline;
			line-height: 18px;

			&-text {
				font-size: 14px;
				margin-right: 2px;
				color: #333333;
			}

			&-link {
				display: inline;
				color: #438451;
				text-decoration: none;
				cursor: pointer;
				font-size: 14px;
				margin-right: 4px;

				&:hover {
					text-decoration: underline;
				}
			}

			&-divider {
				display: inline;
				color: #4D4D4D;
				font-weight: 500;
				font-size: 14px;
			}

			&-show-more {
				display: inline-block;
				background-color: #F2F2F2;
				font-size: 12px;
				line-height: 16px;
				padding: 0 4px;
				border-radius: 8px;
				cursor: pointer;
				color: #979797;

				&:hover {
					background-color: #F3F8F4;
					color: #438451;
				}
			}
		}
	}

	&__additions-collapsed {
		display: flex;
		align-items: center;
		gap: 2px;
		padding: 4px 4px 8px 0;
		cursor: pointer;
		color: #979797;
		font-size: 12px;
		line-height: 16px;

		&:hover {
			color: #666;
		}
	}

	&__additions {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 4px;
	}

	&__addition {
		display: flex;
		flex-direction: column;
		gap: 2px;

		&-header {
			display: flex;
			align-items: baseline;
			gap: 6px;
		}

		&-user {
			font-weight: 400;
			font-size: 14px;
			line-height: 18px;
			color: #4B7053;
		}

		&-date {
			font-size: 12px;
			line-height: 16px;
			color: #2A232380;
			white-space: nowrap;
		}

		&-message {
			font-size: 14px;
			line-height: 18px;
			color: #333333;
		}

		&-title {
			font-weight: 500;
			color: #4D4D4D;
		}
	}

	&__comment {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;

		&-label {
			margin-top: 4px;
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
			color: #979797;
		}

		&-input {
			display: flex;
			align-items: center;
			gap: 4px;
		}
	}

	&__create-time {
		color: #D2D2D2;
	}

	&__footer-bottom {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 4px;
		font-size: 14px;
		line-height: 18px;
		color: #979797;

		:deep(.app-tooltip__activator) {
			display: flex;
		}

		&-date {
			color: #979797;
			display: inline-flex;
			align-items: center;

			&--lost {
				color: #EE966A !important;

				svg {
					path {
						color: #EE966A !important;
					}
				}
			}

			&--success {
				color: #6DA279 !important;

				svg {
					path {
						color: #6DA279 !important;
					}
				}
			}

			&-clock {
				cursor: auto;
				margin-top: -2px;
				margin-right: 4px;

				&:hover {
					background: transparent;
				}
			}
		}
	}
}

</style>
